/**
 * Patient Onboarding API Service
 * Handles all API calls related to patient onboarding and profile management
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Use proxy for development, fallback to config
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
const PATIENT_API = `${BASE_URL}/api/v1/patient`;

// Individual validation functions
const validateFullName = (fullName) => {
  if (!fullName || fullName.trim() === '') {
    return 'Full name is required';
  }
  if (fullName.length < 2) {
    return 'Full name must be at least 2 characters';
  }
  if (fullName.length > 160) {
    return 'Full name cannot exceed 160 characters';
  }
  return null;
};

const validatePhoneE164 = (phone) => {
  if (!phone || phone.trim() === '') {
    return null; // Optional field
  }
  // E.164 format validation: + followed by 7-15 digits
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  if (!phoneRegex.test(phone)) {
    return 'Phone number must be in E.164 format (e.g., +911234567890)';
  }
  return null;
};

const validateDateOfBirth = (dob) => {
  if (!dob) {
    return null; // Optional field
  }
  
  const date = new Date(dob);
  const now = new Date();
  const minDate = new Date(now.getFullYear() - 120, 0, 1); // 120 years ago
  
  if (date > now) {
    return 'Date of birth cannot be in the future';
  }
  if (date < minDate) {
    return 'Age cannot exceed 120 years';
  }
  return null;
};

const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return null; // Optional field
  }
  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};

const validatePostalCode = (postalCode) => {
  if (!postalCode || postalCode.trim() === '') {
    return null; // Optional field
  }
  if (!isValidPostalCode(postalCode)) {
    return 'Please enter a valid postal code';
  }
  return null;
};

// Validation object with individual methods
export const validateProfile = {
  fullName: validateFullName,
  phoneE164: validatePhoneE164,
  dateOfBirth: validateDateOfBirth,
  email: validateEmail,
  postalCode: validatePostalCode,
  
  // Legacy function for backward compatibility
  validate: (profileData) => {
    const errors = {};
    
    // Required fields - only validate if user is trying to save
    if (profileData.fullName !== undefined) {
      const fullNameError = validateFullName(profileData.fullName);
      if (fullNameError) errors.fullName = fullNameError;
    }
    
    if (profileData.phoneE164 !== undefined) {
      const phoneError = validatePhoneE164(profileData.phoneE164);
      if (phoneError) errors.phoneE164 = phoneError;
    }
    
    if (profileData.dateOfBirth !== undefined) {
      const dobError = validateDateOfBirth(profileData.dateOfBirth);
      if (dobError) errors.dateOfBirth = dobError;
    }
    
    if (profileData.email !== undefined) {
      const emailError = validateEmail(profileData.email);
      if (emailError) errors.email = emailError;
    }
    
    if (profileData.postalCode !== undefined) {
      const postalError = validatePostalCode(profileData.postalCode);
      if (postalError) errors.postalCode = postalError;
    }
    
    return errors;
  }
};

// Helper functions
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPostalCode = (postalCode) => {
  // Basic postal code validation (can be enhanced for specific countries)
  return postalCode.length >= 3 && postalCode.length <= 10;
};

// Format profile data for API
export const formatProfileData = (profileData) => {
  return {
    full_name: profileData.fullName,
    dob: profileData.dateOfBirth ? profileData.dateOfBirth.toISOString().split('T')[0] : null,
    gender: profileData.gender,
    phone_e164: profileData.phoneE164,
    email: profileData.email,
    address_line1: profileData.addressLine1,
    address_line2: profileData.addressLine2,
    city: profileData.city,
    state: profileData.state,
    postal_code: profileData.postalCode,
    country_code: profileData.countryCode,
    emergency_contact_name: profileData.emergencyContactName,
    emergency_contact_phone_e164: profileData.emergencyContactPhone,
    blood_group: profileData.bloodGroup,
    marital_status: profileData.maritalStatus,
    education: profileData.education,
    employment_status: profileData.employmentStatus,
    occupation: profileData.occupation,
    employer: profileData.employer,
    insurance_provider: profileData.insuranceProvider,
    insurance_policy_number: profileData.insurancePolicyNumber,
    insurance_group_number: profileData.insuranceGroupNumber
  };
};

// Parse profile data from API
export const parseProfileData = (apiData) => {
  return {
    fullName: apiData.full_name || '',
    dateOfBirth: apiData.dob ? new Date(apiData.dob) : null,
    gender: apiData.gender || '',
    phoneE164: apiData.phone_e164 || '',
    email: apiData.email || '',
    addressLine1: apiData.address_line1 || '',
    addressLine2: apiData.address_line2 || '',
    city: apiData.city || '',
    state: apiData.state || '',
    postalCode: apiData.postal_code || '',
    countryCode: apiData.country_code || 'IN',
    emergencyContactName: apiData.emergency_contact_name || '',
    emergencyContactPhone: apiData.emergency_contact_phone_e164 || '',
    bloodGroup: apiData.blood_group || '',
    maritalStatus: apiData.marital_status || '',
    education: apiData.education || '',
    employmentStatus: apiData.employment_status || '',
    occupation: apiData.occupation || '',
    employer: apiData.employer || '',
    insuranceProvider: apiData.insurance_provider || '',
    insurancePolicyNumber: apiData.insurance_policy_number || '',
    insuranceGroupNumber: apiData.insurance_group_number || ''
  };
};

export const patientOnboardingService = {
  // Get patient profile (returns empty structure if none exists)
  getProfile: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient profile:', error);
      throw error;
    }
  },

  // Create or update patient profile (idempotent)
  saveProfile: async (profileData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/profile/`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error saving patient profile:', error);
      throw error;
    }
  },

  // Get onboarding state and next actions (unified endpoint)
  getOnboardingState: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/auth/onboarding/state/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching onboarding state:', error);
      throw error;
    }
  },

  // Mark an onboarding section as complete
  completeSection: async (sectionKey) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/onboarding/complete-section/`, {
        key: sectionKey
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error completing section:', error);
      throw error;
    }
  },

  // Check if intake forms are available
  getIntakeAvailability: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/onboarding/intake/availability/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking intake availability:', error);
      throw error;
    }
  },

  // Get profile completion details
  getProfileCompletion: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/profile/completion/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching profile completion:', error);
      throw error;
    }
  },

  // Create trial (internal use)
  createTrial: async (source = 'self_signup', durationDays = 14) => {
    try {
      const response = await axios.post(`${PATIENT_API}/onboarding/create-trial/`, {
        source,
        duration_days: durationDays
      });
      return response.data;
    } catch (error) {
      console.error('Error creating trial:', error);
      throw error;
    }
  }
};




